import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

const FAQ = ({ showTitle = true }) => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqData = [
    {
      question: "What is baad.tv?",
      answer: "baad.tv is a social network designed specifically for TV lovers. Think of it as Facebook for your TV shows! Track what you're watching, share reviews with friends, get personalised recommendations, and discover what your flock is binge-watching. It's the best way to organise your TV life and connect with fellow TV enthusiasts."
    },
    {
      question: "Is baad.tv free to use?",
      answer: "Yes! baad.tv is completely free. Track unlimited shows, share reviews, follow friends, and get recommendations at no cost. We believe everyone should have access to a great TV tracking and social experience."
    },
    {
      question: "How do I start tracking my TV shows?",
      answer: "It's easy! After signing in with Google, click 'Add Show' on your My Shows page. Search for any TV show using our integrated TMDB database, select it, and choose your status (Currently Watching, Waiting for Next Season, Completed, or Want to Watch). You can also add your personal rating, review, and note which streaming service you're watching on."
    },
    {
      question: "Can I share my TV show reviews with friends?",
      answer: "Absolutely! Once you've added a rating and review to a show, you can share it to the social feed where all your friends (and the entire flock) can see it. Friends can like and comment on your reviews, creating engaging conversations about your favourite shows. You can also recommend shows directly to specific friends."
    },
    {
      question: "How do I follow friends on baad.tv?",
      answer: "Go to the Friends page and use the search bar to find friends by name or username. Click the follow button on their profile. Once you follow someone, you'll see their reviews in your social feed and can view their watching statistics. They'll also receive a notification that you followed them!"
    },
    {
      question: "What are the different show statuses I can use?",
      answer: "baad.tv offers four status categories to organise your shows: 'Currently Watching' for active shows, 'Waiting for Next Season' for shows between seasons, 'Completed' for finished series or shows you've completed watching, and 'Want to Watch' for shows on your watchlist. You can easily update a show's status anytime."
    },
    {
      question: "How do recommendations work?",
      answer: "You can recommend shows directly to friends through the recommend feature. Your friend will receive an in-app notification about your recommendation. It's a great way to share your favourite discoveries and help friends find their next binge-worthy show!"
    },
    {
      question: "Can I see what my friends are watching?",
      answer: "Yes! The social feed (Rated by the Flock) shows all reviews and ratings from people you follow. You can also visit any friend's profile to see their complete show collection, viewing statistics, and all their reviews. It's like having a personal TV guide curated by people you trust."
    },
    {
      question: "What streaming services does baad.tv support?",
      answer: "baad.tv supports all major streaming platforms including Netflix, Disney+, Amazon Prime Video, Hulu, HBO Max, Apple TV+, Paramount+, Peacock, and many more. You can also add custom streaming services if you're watching on a platform not in our list."
    },
    {
      question: "How are show ratings calculated?",
      answer: "Each show displays three types of ratings: your personal rating (1-10 scale), the average rating from your friends who've reviewed it, and the average rating from all baad.tv users. This gives you multiple perspectives to help decide what to watch next!"
    },
    {
      question: "Is my data private and secure?",
      answer: "Yes, your privacy is important to us. We use Google OAuth for secure authentication, and your data is stored securely. You control what you share - only reviews you actively share to the feed are visible to others. Your personal show list, ratings, and notes remain private unless you choose to share them."
    },
    {
      question: "Can I use baad.tv on mobile devices?",
      answer: "Absolutely! baad.tv is fully responsive and optimized for mobile browsers (Safari, Chrome, Firefox). Simply visit baad.tv on your phone or tablet and enjoy the same great experience. We recommend using Safari on iOS for the best authentication experience."
    },
    {
      question: "How do I change my username?",
      answer: "After your first login, you'll be prompted to set a username (minimum 4 characters). If you need to change it later, you can update it through your profile settings. Your username is how friends can find and identify you on baad.tv."
    },
    {
      question: "What's the difference between liking and commenting on reviews?",
      answer: "Liking a review is a quick way to show appreciation or agreement with someone's opinion. Commenting allows you to start a conversation, share your own thoughts, or discuss the show in more detail. Both actions send notifications to the review author!"
    },
    {
      question: "Can I delete my account?",
      answer: "Yes, you have full control over your data. If you wish to delete your account, you can do so from your account settings. This will permanently remove all your shows, reviews, and social interactions from baad.tv."
    },
    {
      question: "How accurate is the show information?",
      answer: "We integrate with The Movie Database (TMDB), one of the most comprehensive and up-to-date sources for TV show information. This ensures you get accurate show details, posters, release dates, and genre information. You can also update show details using the 'Update Show Details' button."
    },
    {
      question: "What makes baad.tv different from other TV trackers?",
      answer: "Unlike traditional TV trackers, baad.tv puts social connection at the centre. It's not just about tracking shows - it's about sharing your TV journey with friends, getting personalised recommendations from people you trust, and being part of a community of TV lovers (the flock!). Plus, our unique sheep theme and playful branding make tracking TV shows fun!"
    },
    {
      question: "How do I report a bug or suggest a feature?",
      answer: "We'd love to hear from you! You can reach out through our social media channels or contact the development team directly. We're continuously improving baad.tv and value user feedback to make the platform better for everyone."
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="faq-container">
      {showTitle && (
        <div className="faq-header">
          <h2>Frequently Asked Questions</h2>
          <p className="faq-subtitle">Everything you need to know about baad.tv</p>
        </div>
      )}
      
      <div className="faq-list">
        {faqData.map((faq, index) => (
          <div 
            key={index} 
            className={`faq-item ${openIndex === index ? 'active' : ''}`}
          >
            <button 
              className="faq-question"
              onClick={() => toggleFAQ(index)}
              aria-expanded={openIndex === index}
            >
              <span>{faq.question}</span>
              {openIndex === index ? (
                <ChevronUp size={20} className="faq-icon" />
              ) : (
                <ChevronDown size={20} className="faq-icon" />
              )}
            </button>
            
            {openIndex === index && (
              <div className="faq-answer">
                <p>{faq.answer}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      <style jsx>{`
        .faq-container {
          width: 100%;
          max-width: 900px;
          margin: 0 auto;
          padding: 3rem 1.5rem;
        }

        .faq-header {
          text-align: center;
          margin-bottom: 3rem;
        }

        .faq-header h2 {
          font-size: 2.5rem;
          font-weight: 700;
          color: #ffffff;
          margin-bottom: 0.5rem;
        }

        .faq-subtitle {
          font-size: 1.1rem;
          color: #b0b0b0;
        }

        .faq-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .faq-item {
          background: #1a1a1a;
          border: 1px solid #333333;
          border-radius: 8px;
          overflow: hidden;
          transition: all 0.3s ease;
        }

        .faq-item.active {
          border-color: #E62F88;
          box-shadow: 0 4px 12px rgba(230, 47, 136, 0.2);
        }

        .faq-question {
          width: 100%;
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.25rem 1.5rem;
          background: transparent;
          border: none;
          color: #ffffff;
          font-size: 1.1rem;
          font-weight: 600;
          text-align: left;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .faq-question:hover {
          color: #E62F88;
        }

        .faq-icon {
          color: #E62F88;
          flex-shrink: 0;
          margin-left: 1rem;
          transition: transform 0.3s ease;
        }

        .faq-item.active .faq-icon {
          transform: rotate(180deg);
        }

        .faq-answer {
          padding: 0 1.5rem 1.5rem 1.5rem;
          animation: slideDown 0.3s ease-out;
        }

        .faq-answer p {
          color: #b0b0b0;
          line-height: 1.7;
          font-size: 1rem;
          margin: 0;
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @media (max-width: 768px) {
          .faq-container {
            padding: 2rem 1rem;
          }

          .faq-header h2 {
            font-size: 1.75rem;
          }

          .faq-subtitle {
            font-size: 1rem;
          }

          .faq-question {
            padding: 1rem;
            font-size: 1rem;
          }

          .faq-answer {
            padding: 0 1rem 1rem 1rem;
          }

          .faq-answer p {
            font-size: 0.95rem;
          }
        }
      `}</style>
    </div>
  );
};

export default FAQ;
